import { create } from 'zustand';
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  addQuarters,
  subQuarters,
  startOfYear,
  endOfYear,
  addYears,
  subYears,
  format,
} from 'date-fns';
import { useScheduleStore } from './scheduleStore';
import type {
  TimesheetGroupBy,
  TimesheetRow,
  CapacityMap,
  TimesheetDateRange,
  TimesheetFilters,
  TimesheetPeriod,
  ProgressGroup,
} from '../types/timesheet.types';
import { GROUPING_REGISTRY } from '../config/timesheet-grouping';

// ─── Persistence ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'rp_timesheet_state';

interface PersistedState {
  groupBy: TimesheetGroupBy;
  subGroupBy: 'category' | null;
  period: TimesheetPeriod;
  sidebarCollapsed: boolean;
  filters: {
    userIds: string[];
    boardIds: string[];
    teamIds: number[];
  };
}

function loadPersisted(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const groupBy = parsed.groupBy ?? 'summary';
      const isValid = GROUPING_REGISTRY.some((g) => g.key === groupBy);
      const VALID_PERIODS: TimesheetPeriod[] = [
        'current_week', 'previous_week', 'current_month', 'previous_month',
        'current_quarter', 'last_quarter', 'current_year', 'last_year', 'custom',
      ];
      const period = VALID_PERIODS.includes(parsed.period) ? parsed.period : 'current_week';
      const subGroupBy = parsed.subGroupBy === 'category' ? 'category' : null;
      return {
        groupBy: isValid ? groupBy : 'summary',
        subGroupBy,
        period,
        sidebarCollapsed: parsed.sidebarCollapsed ?? false,
        filters: {
          userIds: Array.isArray(parsed.filters?.userIds) ? parsed.filters.userIds : [],
          boardIds: Array.isArray(parsed.filters?.boardIds) ? parsed.filters.boardIds : [],
          teamIds: Array.isArray(parsed.filters?.teamIds) ? parsed.filters.teamIds : [],
        },
      };
    }
  } catch {
    /* ignore */
  }
  return {
    groupBy: 'summary',
    subGroupBy: null,
    period: 'current_week' as TimesheetPeriod,
    sidebarCollapsed: false,
    filters: { userIds: [], boardIds: [], teamIds: [] },
  };
}

function persist(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function persistPartial(partial: Partial<PersistedState>) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...partial }));
  } catch {
    /* ignore */
  }
}

// ─── Period → Date Range Resolution ─────────────────────────────────────────

/**
 * Resolves a TimesheetPeriod to a concrete date range.
 * For `custom`, the existing custom range is returned as-is.
 */
function resolvePeriodRange(
  period: TimesheetPeriod,
  customRange?: TimesheetDateRange,
): TimesheetDateRange {
  const today = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  const weekStartsOn = useScheduleStore.getState().firstDayOfWeek;

  switch (period) {
    case 'current_week':
      return {
        start: fmt(startOfWeek(today, { weekStartsOn })),
        end: fmt(endOfWeek(today, { weekStartsOn })),
      };
    case 'previous_week': {
      const prev = subWeeks(today, 1);
      return {
        start: fmt(startOfWeek(prev, { weekStartsOn })),
        end: fmt(endOfWeek(prev, { weekStartsOn })),
      };
    }
    case 'current_month':
      return { start: fmt(startOfMonth(today)), end: fmt(endOfMonth(today)) };
    case 'previous_month': {
      const prev = subMonths(today, 1);
      return { start: fmt(startOfMonth(prev)), end: fmt(endOfMonth(prev)) };
    }
    case 'current_quarter':
      return { start: fmt(startOfQuarter(today)), end: fmt(endOfQuarter(today)) };
    case 'last_quarter': {
      const prev = subQuarters(today, 1);
      return { start: fmt(startOfQuarter(prev)), end: fmt(endOfQuarter(prev)) };
    }
    case 'current_year':
      return { start: fmt(startOfYear(today)), end: fmt(endOfYear(today)) };
    case 'last_year': {
      const prev = subYears(today, 1);
      return { start: fmt(startOfYear(prev)), end: fmt(endOfYear(prev)) };
    }
    case 'custom':
      return customRange ?? {
        start: fmt(startOfWeek(today, { weekStartsOn })),
        end: fmt(endOfWeek(today, { weekStartsOn })),
      };
    default:
      return {
        start: fmt(startOfWeek(today, { weekStartsOn })),
        end: fmt(endOfWeek(today, { weekStartsOn })),
      };
  }
}

/**
 * Shift a date range by +1 / -1 unit corresponding to the period.
 * E.g., if period is 'current_month' and direction is 1, move one month forward.
 * For 'custom', shifts by the same number of days as the range width.
 */
function shiftDateRange(
  range: TimesheetDateRange,
  period: TimesheetPeriod,
  direction: 1 | -1,
): TimesheetDateRange {
  const start = new Date(range.start);
  const end = new Date(range.end);
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  const shiftFn = direction === 1 ? addWeeks : subWeeks;

  switch (period) {
    case 'current_week':
    case 'previous_week':
      return { start: fmt(shiftFn(start, 1)), end: fmt(shiftFn(end, 1)) };
    case 'current_month':
    case 'previous_month': {
      const fn = direction === 1 ? addMonths : subMonths;
      const newStart = fn(start, 1);
      return { start: fmt(startOfMonth(newStart)), end: fmt(endOfMonth(newStart)) };
    }
    case 'current_quarter':
    case 'last_quarter': {
      const fn = direction === 1 ? addQuarters : subQuarters;
      const newStart = fn(start, 1);
      return { start: fmt(startOfQuarter(newStart)), end: fmt(endOfQuarter(newStart)) };
    }
    case 'current_year':
    case 'last_year': {
      const fn = direction === 1 ? addYears : subYears;
      const newStart = fn(start, 1);
      return { start: fmt(startOfYear(newStart)), end: fmt(endOfYear(newStart)) };
    }
    case 'custom': {
      // Shift by the width of the custom range
      const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const ms = days * 24 * 60 * 60 * 1000 * direction;
      return {
        start: fmt(new Date(start.getTime() + ms)),
        end: fmt(new Date(end.getTime() + ms)),
      };
    }
    default:
      return { start: fmt(shiftFn(start, 1)), end: fmt(shiftFn(end, 1)) };
  }
}

// ─── Store ───────────────────────────────────────────────────────────────────

export interface TimesheetState {
  // View configuration
  groupBy: TimesheetGroupBy;
  subGroupBy: 'category' | null;
  period: TimesheetPeriod;
  dateRange: TimesheetDateRange;
  filters: TimesheetFilters;

  // Data
  rows: TimesheetRow[];
  capacityMap: CapacityMap;
  dateColumns: string[];

  // UI state
  expandedRowKeys: Set<string>;
  sidebarCollapsed: boolean;
  isLoading: boolean;
  error: string | null;

  // Progress view data
  progressGroups: ProgressGroup[];
  progressLoading: boolean;
  progressError: string | null;

  // Actions
  setGroupBy: (groupBy: TimesheetGroupBy) => void;
  setSubGroupBy: (subGroupBy: 'category' | null) => void;
  setPeriod: (period: TimesheetPeriod) => void;
  setCustomDateRange: (start: string, end: string) => void;
  setDateRange: (range: TimesheetDateRange) => void;
  navigateDatePrev: () => void;
  navigateDateNext: () => void;
  navigateToToday: () => void;
  setFilters: (filters: Partial<TimesheetFilters>) => void;
  clearFilters: () => void;
  setRows: (rows: TimesheetRow[]) => void;
  setCapacityMap: (map: CapacityMap) => void;
  setDateColumns: (columns: string[]) => void;
  toggleRow: (rowKey: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Progress actions
  setProgressGroups: (groups: ProgressGroup[]) => void;
  setProgressLoading: (loading: boolean) => void;
  setProgressError: (error: string | null) => void;

  // Name caches (persisted across view switches, not in localStorage)
  boardNameMap: Map<string, string>;
  itemNameMap: Map<string, string>;
  mergeBoardNames: (entries: Array<{ id: string; name: string }>) => void;
  mergeItemNames: (entries: Array<{ id: string; name: string }>) => void;
}

const persisted = loadPersisted();

export const useTimesheetStore = create<TimesheetState>((set, get) => ({
  // ── Initial state ────────────────────────────────────────────────────────
  groupBy: persisted.groupBy,
  subGroupBy: persisted.subGroupBy,
  period: persisted.period,
  dateRange: resolvePeriodRange(persisted.period),
  filters: persisted.filters,
  rows: [],
  capacityMap: {},
  dateColumns: [],
  expandedRowKeys: new Set<string>(),
  sidebarCollapsed: persisted.sidebarCollapsed,
  isLoading: false,
  error: null,
  progressGroups: [],
  progressLoading: false,
  progressError: null,

  // ── Actions ──────────────────────────────────────────────────────────────

  setGroupBy: (groupBy) => {
    set({ groupBy, expandedRowKeys: new Set() });
    persistPartial({ groupBy });
  },

  setSubGroupBy: (subGroupBy) => {
    set({ subGroupBy, expandedRowKeys: new Set() });
    persistPartial({ subGroupBy });
  },

  setPeriod: (period) => {
    const dateRange = resolvePeriodRange(period);
    set({ period, dateRange });
    persistPartial({ period });
  },

  setCustomDateRange: (start, end) => {
    set({ period: 'custom' as TimesheetPeriod, dateRange: { start, end } });
    persistPartial({ period: 'custom' as TimesheetPeriod });
  },

  setDateRange: (dateRange) => set({ dateRange }),

  navigateDatePrev: () => {
    set((state) => {
      const dateRange = shiftDateRange(state.dateRange, state.period, -1);
      return { dateRange };
    });
  },

  navigateDateNext: () => {
    set((state) => {
      const dateRange = shiftDateRange(state.dateRange, state.period, 1);
      return { dateRange };
    });
  },

  navigateToToday: () => {
    set((state) => {
      const dateRange = resolvePeriodRange(state.period);
      return { dateRange };
    });
  },

  setFilters: (partial) => {
    set((state) => {
      const newFilters = { ...state.filters, ...partial };
      persistPartial({
        filters: {
          userIds: newFilters.userIds,
          boardIds: newFilters.boardIds,
          teamIds: newFilters.teamIds ?? [],
        },
      });
      return { filters: newFilters };
    });
  },

  clearFilters: () => {
    const emptyFilters = { userIds: [], boardIds: [], teamIds: [] };
    set({ filters: emptyFilters });
    persistPartial({ filters: emptyFilters });
  },

  setRows: (rows) => set({ rows }),

  setCapacityMap: (capacityMap) => set({ capacityMap }),

  setDateColumns: (dateColumns) => set({ dateColumns }),

  toggleRow: (rowKey) =>
    set((state) => {
      const next = new Set(state.expandedRowKeys);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return { expandedRowKeys: next };
    }),

  expandAll: () =>
    set((state) => {
      const keys = new Set<string>();
      for (const row of state.rows) {
        if (row.expandable) keys.add(row.rowKey);
      }
      return { expandedRowKeys: keys };
    }),

  collapseAll: () => set({ expandedRowKeys: new Set() }),

  setSidebarCollapsed: (sidebarCollapsed) => {
    set({ sidebarCollapsed });
    persistPartial({ sidebarCollapsed });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  // ── Progress actions ────────────────────────────────────────────────────

  setProgressGroups: (progressGroups) => set({ progressGroups }),
  setProgressLoading: (progressLoading) => set({ progressLoading }),
  setProgressError: (progressError) => set({ progressError }),

  // ── Name caches ──────────────────────────────────────────────────────

  boardNameMap: new Map<string, string>(),
  itemNameMap: new Map<string, string>(),

  mergeBoardNames: (entries) =>
    set((state) => {
      if (entries.length === 0) return state;
      const next = new Map(state.boardNameMap);
      for (const e of entries) next.set(e.id, e.name);
      return { boardNameMap: next };
    }),

  mergeItemNames: (entries) =>
    set((state) => {
      if (entries.length === 0) return state;
      const next = new Map(state.itemNameMap);
      for (const e of entries) next.set(e.id, e.name);
      return { itemNameMap: next };
    }),
}));
